import type { ReactElement } from 'react';
import cx from 'clsx';
import type { CountryCode } from '../../constants/countries';
import flagStyles from '../flag/flag.module.css';
import styles from './avatar.module.css';

const ICON_CLASS = {
  '16': styles.avatarIcon16,
  '20': styles.avatarIcon20,
} satisfies Record<'16' | '20', string | undefined>;

/**
 * Renders a circular flag badge for the Avatar component.
 *
 * Uses the flag sprite directly (no <Flag> component) to keep sizing and
 * clipping fully under Avatar's control. The outer container is a flex circle
 * with `overflow:hidden`; the inner span is intentionally oversized
 * (`flex-shrink:0`) so the flex alignment centers it and the overflow clips it
 * evenly on all sides — a centered circular crop with no positioning tricks.
 */
export function AvatarFlagBadge({
  code,
  iconSize,
}: {
  code: CountryCode;
  iconSize: '16' | '20';
}): ReactElement {
  const flagW = iconSize === '16' ? 22 : 28;
  const flagH = flagW * 0.75;

  return (
    <span
      aria-hidden
      className={cx(styles.avatarIcon, ICON_CLASS[iconSize], styles.avatarFlagContainer)}
    >
      <span
        className={cx(flagStyles.flag, flagStyles[code.toLowerCase()])}
        style={{ width: flagW, height: flagH, flexShrink: 0 }}
      />
    </span>
  );
}
